import { useEffect, useState } from 'react'
import { getFundingSummary } from '../../api/fundingApi'
import { FundingTodo } from '../../types'
import { toTodo } from './toTodo'

interface UseFundingTodosResult {
  todos: FundingTodo[]
  loading: boolean
}

/**
 * Self-contained todos fetch for hosts that render `<FundingToDos>` on its own
 * (e.g. the Salesforce Canvas tile) rather than inside `<FundingDashboard>`,
 * which already fetches the combined summary. Pulls the combined funding
 * summary and derives the ToDo list from its recommendation groups.
 */
export const useFundingTodos = (
  serviceUrl: string | undefined,
  accountId: string | undefined
): UseFundingTodosResult => {
  const [todos, setTodos] = useState<FundingTodo[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!serviceUrl || !accountId) {
      setTodos([])
      setLoading(false)
      return
    }
    // `ignore` drops a stale response if accountId changes or we unmount
    // mid-flight, so a late resolve can't overwrite the newer state.
    let ignore = false
    setLoading(true)
    ;(async () => {
      try {
        const { data } = await getFundingSummary(serviceUrl, accountId)
        if (ignore) return
        setTodos(
          (data.combined.recommendation_groups ?? [])
            .map(toTodo)
            .filter((t): t is FundingTodo => t !== null)
        )
      } catch {
        if (!ignore) setTodos([])
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [serviceUrl, accountId])

  return { todos, loading }
}
